import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { AmeupLoading } from '@/components/ui/ameup-loading';
import { CheckCircle2, XCircle } from 'lucide-react';
import confetti from 'canvas-confetti';

const PAGBANK_OAUTH_STATE_KEY = 'pagbank_oauth_state';
const PAGBANK_OAUTH_REDIRECT_URI_KEY = 'pagbank_oauth_redirect_uri';
const PAGBANK_PENDING_CODE_KEY = 'pagbank_oauth_pending_code';
const PAGBANK_PENDING_STATE_KEY = 'pagbank_oauth_pending_state';
const FINANCES_ROUTE = '/fornecedor/painel/administracao/finances';
const CALLBACK_ROUTE = '/auth/pagbank/callback';

const savePendingOAuthReturn = (code: string, state: string | null) => {
  localStorage.setItem(PAGBANK_PENDING_CODE_KEY, code);
  if (state) {
    localStorage.setItem(PAGBANK_PENDING_STATE_KEY, state);
  }
};

const clearPendingOAuthReturn = () => {
  localStorage.removeItem(PAGBANK_PENDING_CODE_KEY);
  localStorage.removeItem(PAGBANK_PENDING_STATE_KEY);
};

const PagBankCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const processingRef = useRef(false);

  useEffect(() => {
    if (processingRef.current) return;

    const processCallback = async () => {
      processingRef.current = true;

      const code = searchParams.get('code') || localStorage.getItem(PAGBANK_PENDING_CODE_KEY);
      const state = searchParams.get('state') || localStorage.getItem(PAGBANK_PENDING_STATE_KEY);
      const savedState = localStorage.getItem(PAGBANK_OAUTH_STATE_KEY);

      if (state && savedState && state !== savedState) {
        console.error('State mismatch', { received: state, saved: savedState });
        setErrorMsg('Estado OAuth inválido. Tente novamente.');
        setStatus('error');
        return;
      }

      if (!code) {
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error === 'access_denied') {
          setErrorMsg('Você cancelou a autorização do PagBank.');
        } else if (error === 'invalid_scope') {
          setErrorMsg(
            'Permissões OAuth inválidas para esta aplicação PagBank. '
            + 'Confirme que o ambiente (sandbox/produção), client_id e scopes estão corretos no painel Desenvolvedor.',
          );
        } else if (error) {
          setErrorMsg(
            errorDescription
              ? `${error}: ${errorDescription}`
              : `PagBank retornou erro: ${error}`,
          );
        } else {
          setErrorMsg('Código de autorização não recebido.');
        }

        setStatus('error');
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          savePendingOAuthReturn(code, state);
          localStorage.setItem('postLoginRedirect', CALLBACK_ROUTE);
          window.location.href = '/login';
          return;
        }

        const redirectUri = localStorage.getItem(PAGBANK_OAUTH_REDIRECT_URI_KEY);

        const { data, error } = await supabase.functions.invoke('pagbank-connect', {
          body: {
            code,
            ...(redirectUri ? { redirect_uri: redirectUri } : {}),
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (error) {
          console.error('PagBank connect error', { name: (error as any)?.name, status: (error as any)?.status });
          let detailedMessage = error.message || 'Erro ao conectar com o PagBank. Tente novamente.';

          if (error instanceof FunctionsHttpError) {
            try {
              const errorBody = await error.context.json();
              console.error('[PagBank Callback] Edge function error', { hasDetails: !!errorBody?.details, hasError: !!errorBody?.error });
              detailedMessage = errorBody?.details || errorBody?.error || detailedMessage;
            } catch (parseError) {
              console.error('[PagBank Callback] Failed to parse edge function error body', { message: parseError instanceof Error ? parseError.message : 'unknown' });
            }
          }

          setErrorMsg(detailedMessage);
          setStatus('error');
          return;
        }

        if (data?.success) {
          clearPendingOAuthReturn();
          localStorage.removeItem(PAGBANK_OAUTH_STATE_KEY);
          localStorage.removeItem(PAGBANK_OAUTH_REDIRECT_URI_KEY);
          setStatus('success');
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
          setTimeout(() => confetti({ particleCount: 100, spread: 120, origin: { y: 0.5 } }), 300);

          setTimeout(() => {
            window.location.href = FINANCES_ROUTE;
          }, 3000);
        } else {
          setErrorMsg(data?.error || 'Erro desconhecido ao conectar.');
          setStatus('error');
        }
      } catch (err) {
        console.error('PagBank callback error', { message: err instanceof Error ? err.message : 'unknown' });
        setErrorMsg('Erro de conexão. Verifique sua internet e tente novamente.');
        setStatus('error');
      }
    };

    processCallback();
  }, [searchParams]);

  if (status === 'loading') {
    return <AmeupLoading fullScreen message="Conectando sua conta PagBank..." size="lg" />;
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-8 space-y-6">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold">PagBank Conectado! 🎉</h1>
          <p className="text-muted-foreground">
            Sua conta PagBank foi vinculada com sucesso. Seu ACCO foi capturado automaticamente.
          </p>
          <p className="text-sm text-muted-foreground">Redirecionando para Finanças...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md mx-auto p-8 space-y-6">
        <XCircle className="h-16 w-16 text-destructive mx-auto" />
        <h1 className="text-2xl font-bold">Erro na Conexão</h1>
        <p className="text-muted-foreground">{errorMsg}</p>
        <a
          href={FINANCES_ROUTE}
          className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          Voltar para Finanças
        </a>
      </div>
    </div>
  );
};

export default PagBankCallbackPage;
